/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import { useCallback, useEffect, useState } from 'react';

import { restClient } from '../../interface/api/rest-client';
import { useCapabilitiesApi } from '../preview/pages/settings/system/capabilities/useCapabilitiesApi';
import { useWizard } from './useWizard';

const FORWARDED_HEADERS_TYPE_ID = 'http.forwarded';
const BASE_URL_TYPE_ID = 'baseurl';
const HINTS_URL = '/service/rest/internal/ui/onboarding/base-url-hints';
const ACKNOWLEDGE_URL = '/service/rest/internal/ui/onboarding/base-url-hints/acknowledge';

interface BaseUrlOnboardingHints {
  likelyBehindReverseProxy: boolean;
  detectedUrl: string | null;
}

export interface UseConfigureBaseUrlStepResult {
  acceptReverseProxyHeaders: boolean;
  configureBaseUrl: boolean;
  host: string;
  force: boolean;
  detectedUrl: string | null;
  likelyBehindReverseProxy: boolean;
  onToggleAcceptReverseProxyHeaders: (value: boolean) => void;
  onToggleConfigureBaseUrl: (value: boolean) => void;
  onHostChange: (value: string) => void;
  onToggleForce: (value: boolean) => void;
  onFormSubmit: (event: React.FormEvent) => void;
}

export function useConfigureBaseUrlStep(): UseConfigureBaseUrlStepResult {
  const { registerStep, submit } = useWizard();
  const { fetchCapabilities, createCapability, updateCapability } = useCapabilitiesApi();

  const [acceptReverseProxyHeaders, setAcceptReverseProxyHeaders] = useState(true);
  const [configureBaseUrl, setConfigureBaseUrl] = useState(false);
  const [host, setHost] = useState('');
  const [force, setForce] = useState(false);
  const [detectedUrl, setDetectedUrl] = useState<string | null>(null);
  const [likelyBehindReverseProxy, setLikelyBehindReverseProxy] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // The hint only signals whether *this* request looked like it came through a reverse proxy
    // (used for the nudge text below) — it must not drive the checkbox default. A proxy that
    // terminates TLS but only sends X-Forwarded-Proto (no X-Forwarded-Host) is common and would
    // make the hint false-negative; defaulting the checkbox off it would then disable the
    // already-enabled http.forwarded capability on submit, flipping generated URLs to http://.
    // The checkbox default comes from what's actually configured right now instead.
    fetchCapabilities()
      .then((capabilities) => {
        if (cancelled) return;
        const existingForwarded = capabilities.find((c) => c.typeId === FORWARDED_HEADERS_TYPE_ID);
        setAcceptReverseProxyHeaders(existingForwarded ? existingForwarded.enabled : true);
      })
      .catch((error) => {
        console.warn('[ConfigureBaseUrlStep] Failed to fetch existing capabilities', error);
      });

    restClient
      .get<BaseUrlOnboardingHints>(HINTS_URL)
      .then((hints) => {
        if (cancelled) return;
        setLikelyBehindReverseProxy(hints.likelyBehindReverseProxy);
        setDetectedUrl(hints.detectedUrl ?? null);
        if (hints.detectedUrl) {
          setHost(hints.detectedUrl);
        }
      })
      .catch((error) => {
        console.warn('[ConfigureBaseUrlStep] Failed to fetch onboarding hints', error);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchCapabilities]);

  // "Force" and "Accept reverse proxy headers" are mutually exclusive: a Base URL that
  // overrides the request makes trusting the request's forwarded headers moot, and vice versa.
  const onToggleAcceptReverseProxyHeaders = useCallback((value: boolean) => {
    setAcceptReverseProxyHeaders(value);
    if (value) {
      setForce(false);
    }
  }, []);

  const onToggleForce = useCallback((value: boolean) => {
    setForce(value);
    if (value) {
      setAcceptReverseProxyHeaders(false);
    }
  }, []);

  const onToggleConfigureBaseUrl = useCallback((value: boolean) => {
    setConfigureBaseUrl(value);
    if (!value) {
      setForce(false);
    }
  }, []);

  const onHostChange = useCallback((value: string) => {
    setHost(value);
  }, []);

  const onSubmit = useCallback(async () => {
    const existing = await fetchCapabilities();
    const existingForwarded = existing.find((c) => c.typeId === FORWARDED_HEADERS_TYPE_ID);
    const existingBaseUrl = existing.find((c) => c.typeId === BASE_URL_TYPE_ID);

    // http.forwarded is auto-created on every instance by ForwardedRequestCapabilityBooter, so
    // its existence can't signal "decided" the way a freshly-created baseurl capability can —
    // that's what the explicit acknowledge call below is for. Still apply whatever the admin
    // chose for it here.
    if (existingForwarded) {
      await updateCapability({
        id: existingForwarded.id,
        typeId: FORWARDED_HEADERS_TYPE_ID,
        enabled: acceptReverseProxyHeaders,
        properties: {},
      });
    } else {
      await createCapability({
        typeId: FORWARDED_HEADERS_TYPE_ID,
        enabled: acceptReverseProxyHeaders,
        properties: {},
      });
    }

    if (configureBaseUrl && host.trim().length > 0) {
      const properties = { url: host.trim(), force: String(force) };
      if (existingBaseUrl) {
        await updateCapability({
          id: existingBaseUrl.id,
          typeId: BASE_URL_TYPE_ID,
          enabled: true,
          properties,
        });
      } else {
        await createCapability({
          typeId: BASE_URL_TYPE_ID,
          enabled: true,
          properties,
        });
      }
    }

    // Submitting at all — even leaving every field at its default — is the decision. This is
    // what actually stops ConfigureBaseUrlOnboardingItem.applies() from reappearing; capability
    // existence alone cannot (see that class's Javadoc for why).
    await restClient.put(ACKNOWLEDGE_URL);
  }, [
    fetchCapabilities,
    updateCapability,
    createCapability,
    acceptReverseProxyHeaders,
    configureBaseUrl,
    host,
    force,
  ]);

  const valid = !configureBaseUrl || host.trim().length > 0;

  useEffect(() => {
    registerStep({ valid, onSubmit });
  }, [registerStep, valid, onSubmit]);

  const onFormSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      submit();
    },
    [submit]
  );

  return {
    acceptReverseProxyHeaders,
    configureBaseUrl,
    host,
    force,
    detectedUrl,
    likelyBehindReverseProxy,
    onToggleAcceptReverseProxyHeaders,
    onToggleConfigureBaseUrl,
    onHostChange,
    onToggleForce,
    onFormSubmit,
  };
}
